"use client";

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  SequentialPaletteId,
  getSequentialColor,
  resolveSeriesColor,
} from "@/lib/palettes";
import numeral from "numeral";
import React, { Suspense, lazy, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Pie,
  PieChart,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
} from "recharts";

import { VirtualCCTTable } from "@/components/dashboard/widgets/cct";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CHART_APPEARANCE_DEFAULTS } from "@/constants/widget-types";
import { IWidgetAppearance, WidgetDataPayload } from "@/types/dashboard.types";
import { AlertCircle, BarChart3, TrendingUp } from "lucide-react";
import CalendarHeatmap from "react-calendar-heatmap";
import "react-calendar-heatmap/dist/styles.css";
import { useTranslation } from 'react-i18next';

// Lazy load heavy components to reduce initial bundle
const MapWidget = lazy(() => import("@/components/dashboard/widgets/map/map-widget"));
const BubbleMapWidget = lazy(() => import("@/components/dashboard/widgets/bubble-map/bubble-map-widget"));
const FlowMapWidget = lazy(() => import("@/components/dashboard/widgets/flow-map/flow-map-widget"));

// ------------------------------------
// Formatters
// ------------------------------------
function isValidNumber(str: any) {
        return !isNaN(Number(str)) && str !== ''; // Also check for empty string
    }
const isDateTimeString = (value: any): boolean => {
  if (typeof value !== "string") return false;

  // Try parsing
  const timestamp = Date.parse(value);

  // Valid if parseable and results in a real date
  if (isNaN(timestamp)) return false;

  // Double-check with Date object (avoids edge cases like "0000-00-00")
  const d = new Date(timestamp);
  return d.toString() !== "Invalid Date";
};

export const formatXAxisTick = (tick: any): string => {
  if (isValidNumber(tick)) {
    return numeral(Number(tick)).format("0.[0]a"); // e.g., 44.7M, 1.2K
  }
  if (typeof tick === "string") {
    if (isDateTimeString(tick)) {
      const date = new Date(tick);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
    }
    if (tick.length > 6) {
      return tick.substring(0, 6);
    }
    return tick;
  }
  return String(tick);
};

export const formatTooltipLabel = (label: any): string => {
  if (isValidNumber(label)) {
    return numeral(Number(label)).format("0.[0]a"); // e.g., 44.7M, 1.2K
  }
  if (typeof label === "string" && isDateTimeString(label)) {
    const date = new Date(label);
    return date.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
      timeZone: "UTC",
    });
  }
  return String(label);
};

// ------------------------------------

// Color palette integration helper
const getChartColors = (
  series: any[],
  appearance: IWidgetAppearance
): string[] => {
  return series.map((s, index) => {
    // Check for individual metric color override
    if (s.metricId && appearance.customColors?.[s.metricId]) {
      return appearance.customColors[s.metricId];
    }

    // Use palette system
    return resolveSeriesColor(
      index,
      appearance.paletteMode,
      appearance.presetCategoricalPaletteId,
      appearance.customColors
    );
  });
};

// @ts-ignore
const formatValue = (v: any) =>
  v === null || v === undefined
    ? "-"
    : typeof v === "number"
    ? v.toLocaleString()
    : String(v);

// @ts-ignore
const formatDate = (d: any) => new Date(d).toLocaleDateString();

const EmptyState = ({ msg }: { msg: string }) => (
  <div className="flex h-full flex-col items-center justify-center p-3 sm:p-4 md:p-6 text-xs sm:text-sm text-muted-foreground">
    <BarChart3 className="mb-2 h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-gray-300" />
    <p className="text-center px-2">{msg}</p>
  </div>
);

const ErrorState = ({ errs }: { errs: string[] }) => {
  const { t } = useTranslation();
  return (
    <div className="p-3 sm:p-4 md:p-6">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle className="text-xs sm:text-sm">{t('processManagement.error')}</AlertTitle>
        <AlertDescription className="text-xs sm:text-sm">{errs.join(", ")}</AlertDescription>
      </Alert>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="h-full w-full p-3 sm:p-4 md:p-6">
    <div className="h-full min-h-[140px] w-full animate-pulse rounded bg-muted" />
  </div>
);

// ------------------------------------
// Preview Data Generator
// ------------------------------------
// Provides safe, meaningful preview data so previews render inside resizable
// grid items without overflow.
function createPreviewData(widget: any): WidgetDataPayload | null {
  const type = widget?.visualizationType || "bar";

  if (type === "card") {
    return {
      type: "card",
      value: 12890,
      statLabel: "Total Sales",
    } as WidgetDataPayload;
  }

  if (type === "bar") {
    const categories = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    const seriesNames =
      widget?.metrics?.length > 0
        ? widget.metrics.map((m: any) => m.label || m.name || "Series")
        : ["Revenue", "Cost"];
    return {
      type: "bar",
      categories,
      series: seriesNames.map((name: string, idx: number) => ({
        name,
        data: categories.map((_, i) =>
          Math.round(500 + Math.sin(i + idx) * 200 + i * 50)
        ),
      })),
    } as WidgetDataPayload;
  }

  if (type === "line") {
    const x = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const seriesNames =
      widget?.metrics?.length > 0
        ? widget.metrics.map((m: any) => m.label || m.name || "Series")
        : ["Signups", "Active"];
    return {
      type: "line",
      x,
      series: seriesNames.map((name: string, idx: number) => ({
        name,
        data: x.map((_, i) => Math.round(50 + Math.cos(i + idx) * 20 + i * 5)),
      })),
    } as WidgetDataPayload;
  }

  if (type === "pie") {
    const slices = [
      { label: "A", value: 45 },
      { label: "B", value: 25 },
      { label: "C", value: 15 },
      { label: "D", value: 15 },
    ];
    return {
      type: "pie",
      slices,
    } as WidgetDataPayload;
  }

  if (type === "histogram") {
    const bins = Array.from({ length: 8 }).map((_, i) => ({
      label: `${i * 10}-${i * 10 + 10}`,
    }));
    const seriesNames =
      widget?.metrics?.length > 0
        ? widget.metrics.map((m: any) => m.label || m.name || "Series")
        : ["Values"];
    return {
      type: "histogram",
      bins,
      series: seriesNames.map((name: string) => ({
        name,
        data: bins.map(() => Math.round(Math.random() * 100)),
      })),
    } as WidgetDataPayload;
  }

  if (type === "scatter") {
    const seriesNames =
      widget?.metrics?.length > 0
        ? widget.metrics.map((m: any) => m.label || m.name || "Series")
        : ["Cluster A", "Cluster B"];
    return {
      type: "scatter",
      series: seriesNames.map((name: string, idx: number) => ({
        name,
        points: Array.from({ length: 24 }).map((_, i) => ({
          x: i + idx * 0.5 + Math.random(),
          y: Math.round(40 + Math.sin(i * 0.4 + idx) * 15 + Math.random() * 8),
        })),
      })),
    } as WidgetDataPayload;
  }

  if (type === "calendar-heatmap") {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 180);
    const values = Array.from({ length: 181 }).map((_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return {
        date: d.toISOString().slice(0, 10),
        value: Math.floor(Math.random() * 5),
      };
    });
    return {
      type: "calendar-heatmap",
      startDate: start,
      endDate: end,
      values,
    } as any;
  }

  if (type === "crosstab") {
    const rows = ["Male", "Female"];
    const columns = ["Passed", "Failed"];
    const values = [
      [20, 5],
      [25, 10],
    ];
    return {
      type: "crosstab",
      rows,
      columns,
      values,
      rowTotals: [25, 35],
      colTotals: [45, 15],
      grandTotal: 60,
    } as any;
  }

  if (type === "cct") {
    const factors = ["Gender", "Status"];
    const measures = [
      { id: "price_sum", label: "Price Sum" },
      { id: "qty_count", label: "Qty Count" }
    ];
    const combinations = [
      ["Male", "Passed"],
      ["Male", "Failed"],
      ["Female", "Passed"],
      ["Female", "Failed"],
    ];
    const values = [
      [100, 5],
      [null, 2],
      [150, 8],
      [200, 3],
    ];
    return {
      type: "cct",
      factors,
      measures,
      combinations,
      values,
    } as any;
  }

  if (type === "bubble-map") {
    return {
      type: "bubble-map",
      title: "Bubble Map",
      cities: [
        { country: "Kenya", city: "Nairobi", value: 150 },
        { country: "Nigeria", city: "Lagos", value: 200 },
        { country: "South Africa", city: "Cape Town", value: 120 },
      ],
      meta: {},
      empty: false,
    } as any;
  }
  if (type === "flow-map") {
    return {
      type: "flow-map",
      title: "Flow Map",
      connections: [
        { city1: "Nairobi", city2: "Lagos", value: 500, city1Country: "Kenya", city2Country: "Nigeria" },
        { city1: "Nairobi", city2: "Cape Town", value: 300, city1Country: "Kenya", city2Country: "South Africa" },
      ],
      primaryCities: ["Nairobi"],
      meta: {},
      empty: false,
    } as any;
  }

  return null;
}

// ------------------------------------
// Main Renderer
// ------------------------------------

interface WidgetRendererProps {
  data?: WidgetDataPayload | null | { data: WidgetDataPayload };
  widget?: any;
  loading?: boolean;
  error?: string;
  isPreview?: boolean;
}

export function WidgetRenderer({
  data: rawData,
  widget,
  loading,
  error,
  isPreview,
}: WidgetRendererProps) {
  const { t } = useTranslation();
  const displayData = useMemo(() => {
    let data = rawData as any;
    if (rawData && typeof rawData === "object" && "data" in rawData) {
      data = (rawData as any).data;
    }

    if (isPreview && widget) return createPreviewData(widget);
    return data as WidgetDataPayload | null | undefined;
  }, [isPreview, widget, rawData]);

  const appearance: IWidgetAppearance = useMemo(
    () => ({
      ...CHART_APPEARANCE_DEFAULTS,
      ...(widget?.config?.appearance || {}),
    }),
    [widget?.config?.appearance]
  );

  // Loading / Error / Empty handling
  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorState errs={[error]} />;
  if (!displayData)
    return (
      <EmptyState
        msg={isPreview ? t('processManagement.configureToSeePreview') : t('processManagement.noDataAvailable')}
      />
    );
  if ((displayData as any).empty)
    return <EmptyState msg={t('processManagement.noDataForSelectedCriteria')} />;
  if ((displayData as any).errors?.length)
    return <ErrorState errs={(displayData as any).errors} />;

  // Container: fills parent grid item height and prevents overflow issues.
  return (
    <div className="relative h-full w-full">
      {displayData.type === "card" && (
        <CardView data={displayData as any} appearance={appearance} />
      )}
      {displayData.type === "bar" && (
        <BarView data={displayData as any} appearance={appearance} />
      )}
      {displayData.type === "line" && (
        <LineView data={displayData as any} appearance={appearance} />
      )}
      {displayData.type === "pie" && (
        <PieView data={displayData as any} appearance={appearance} />
      )}
      {displayData.type === "histogram" && (
        <HistogramView data={displayData as any} appearance={appearance} />
      )}
      {displayData.type === "scatter" && (
        <ScatterView data={displayData as any} appearance={appearance} />
      )}
      {displayData.type === "calendar-heatmap" && (
        <CalendarHeatmapView
          data={displayData as any}
          appearance={appearance}
        />
      )}
      {displayData.type === "map" && (
        <Suspense fallback={<LoadingSkeleton />}>
          <MapWidget data={displayData as any} widget={widget} />
        </Suspense>
      )}
      {displayData.type === "bubble-map" && (
        <Suspense fallback={<LoadingSkeleton />}>
          <BubbleMapWidget data={displayData as any} widget={widget} />
        </Suspense>
      )}
      {displayData.type === "flow-map" && (
        <Suspense fallback={<LoadingSkeleton />}>
          <FlowMapWidget data={displayData as any} widget={widget} />
        </Suspense>
      )}
      {displayData.type === "crosstab" && (
        <CrosstabView data={displayData as any} appearance={appearance} widget={widget} />
      )}
      {displayData.type === "cct" && (
        <CCTView data={displayData as any} appearance={appearance} widget={widget} />
      )}
      {![
        "card",
        "bar",
        "line",
        "pie",
        "histogram",
        "scatter",
        "calendar-heatmap",
        "map",
        "bubble-map",
        "flow-map",
        "crosstab",
        "cct",
      ].includes(displayData.type) && (
        <EmptyState
          msg={t('processManagement.unknownWidgetType', { type: displayData.type || 'undefined' })}
        />
      )}
    </div>
  );
}

// ------------------------------------
// Shared types for chart subcomponents
// ------------------------------------

interface ChartViewProps<T> {
  data: T;
  appearance: IWidgetAppearance;
}

// ------------------------------------
// Chart Implementations
// ------------------------------------

const formatCompactNumber = (value: any): string => {
  if (typeof value !== 'number') {
    return "-";
  }
  return numeral(value).format("0,0.[0]a").toUpperCase();
};

const getNumberFormatter = (useCompact: boolean) => {
  return (value: any) => {
    // Check if value is a number before attempting to format
    if (typeof value !== 'number') {
      return String(value);
    }
    return useCompact ? formatCompactNumber(value) : value.toLocaleString();
  };
};


function CardView({
  data,
  appearance,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "card" }>>) {
  const { t } = useTranslation();
  const value = (data as any).value;
  const fullValue = typeof value === 'number' ? value.toLocaleString() : "-";
  const useCompact = appearance?.compactCardValue ?? false;
  const displayValue = useCompact ? formatCompactNumber(value) : fullValue;

  return (
    <div className="flex h-full min-h-[120px] flex-col justify-between p-3 sm:p-4 md:p-6">
      <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
        <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
        <p className="truncate">{(data as any).statLabel || t('processManagement.metric')}</p>
      </div>
      <div className="text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold leading-none tracking-tight text-primary cursor-pointer">
                {displayValue}
              </p>
            </TooltipTrigger>
            <TooltipContent>
              <p>{fullValue}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}

function BarView({
  data,
  appearance,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "bar" }>>) {
  const categories = (data as any).categories as string[];
  const series = (data as any).series as { name: string; data: number[] }[];

  const chartData = useMemo(
    () =>
      categories.map((cat, i) => ({
        category: String(cat),
        ...series.reduce(
          (acc, s) => ({ ...acc, [s.name]: s.data[i] ?? 0 }),
          {}
        ),
      })),
    [categories, series]
  );

  const chartColors = useMemo(
    () => getChartColors(series, appearance),
    [series, appearance]
  );

  const chartConfig = useMemo(
    () =>
      series.reduce(
        (acc, s, i) => ({
          ...acc,
          [s.name?.split(" ")?.join("")]: {
            label: s.name,
            color: chartColors[i],
          },
        }),
        {}
      ),
    [series, chartColors]
  );

  const isHorizontal = appearance.barOrientation === "horizontal";
  
  // For horizontal bars: X shows values (use compactYAxis), Y shows categories
  // For vertical bars: X shows categories, Y shows values (use compactYAxis)
  const valueAxisFormatter = getNumberFormatter(appearance?.compactYAxis ?? false);
  
  // Category formatter handles dates, strings, and optionally compacts numbers
  const categoryAxisFormatter = (value: any): string => {
    // Check if it's a number and apply compact setting
    if (isValidNumber(value)) {
      return appearance?.compactXAxis ? formatCompactNumber(Number(value)) : String(value);
    }
    // Handle date strings
    if (typeof value === "string" && isDateTimeString(value)) {
      const date = new Date(value);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
    }
    // Truncate long strings
    if (typeof value === "string" && value.length > 6) {
      return value.substring(0, 6);
    }
    return String(value);
  };

  return (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <BarChart
        accessibilityLayer
        data={chartData}
        layout={isHorizontal ? "vertical" : "horizontal"}
        // margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
      >
        {appearance.showGrid && (
          <CartesianGrid vertical={!isHorizontal} horizontal={isHorizontal} />
        )}

        {isHorizontal && appearance.showXAxisLabels && (
          <XAxis
            type="number"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            tickFormatter={valueAxisFormatter}
          />
        )}
        {isHorizontal && appearance.showYAxisLabels && (
          <YAxis
            type="category"
            dataKey="category"
            tickLine={false}
            axisLine={false}
            tickMargin={6}
            tickFormatter={categoryAxisFormatter}
            // angle={appearance.xAxisLabelRotation}
            height={appearance.xAxisLabelRotation !== 0 ? 48 : 24}
            width={Math.min(120, Math.max(40, categories?.length * 3))}
          />
        )}

        {!isHorizontal && (
          <XAxis
            dataKey="category"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={categoryAxisFormatter}
            angle={appearance.xAxisLabelRotation}
            height={appearance.xAxisLabelRotation !== 0 ? 48 : 24}
          />
        )}
        {!isHorizontal && appearance.showYAxisLabels && (
          <YAxis allowDecimals tickMargin={6} tickFormatter={valueAxisFormatter} />
        )}
        <ChartTooltip
          cursor={false}
          labelFormatter={formatTooltipLabel}
          content={<ChartTooltipContent />}
        />
        {appearance.legend && series.length > 1 && (
          <ChartLegend content={<ChartLegendContent />} />
        )}
        {series.map((s) => (
          <Bar
            key={s.name}
            dataKey={s.name}
            fill={`var(--color-${s.name?.split(" ")?.join("")})`}
            radius={appearance.barCombinationMode === "stacked" ? 0 : 4}
            stackId={
              appearance.barCombinationMode === "stacked" ? "a" : undefined
            }
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
}

function LineView({
  data,
  appearance,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "line" }>>) {
  const x = (data as any).x as string[];
  const series = (data as any).series as { name: string; data: number[] }[];

  const chartData = useMemo(
    () =>
      x.map((xi, i) => ({
        x: String(xi),
        ...series.reduce(
          (acc, s) => ({ ...acc, [s.name]: s.data[i] ?? null }),
          {}
        ),
      })),
    [x, series]
  );

  const chartColors = useMemo(
    () => getChartColors(series, appearance),
    [series, appearance]
  );

  const chartConfig = useMemo(
    () =>
      series.reduce(
        (acc, s, i) => ({
          ...acc,
          [s.name]: { label: s.name, color: chartColors[i] },
        }),
        {}
      ),
    [series, chartColors]
  );

  const lineStyleMap: Record<string, string | undefined> = {
    solid: undefined,
    dashed: "5 5",
    dotted: "1 5",
  };

  const xAxisFormatter = getNumberFormatter(appearance?.compactXAxis ?? false);
  const yAxisFormatter = getNumberFormatter(appearance?.compactYAxis ?? false);

  return (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <LineChart
        accessibilityLayer
        data={chartData}
        // margin={}
      >
        {appearance.showGrid && <CartesianGrid vertical={false} />}

        {appearance.showXAxisLabels && (
          <XAxis
            dataKey="x"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={xAxisFormatter}
          />
        )}
        {appearance.showYAxisLabels && <YAxis allowDecimals tickMargin={6} tickFormatter={yAxisFormatter} />}
        <ChartTooltip
          cursor={false}
          labelFormatter={formatTooltipLabel}
          content={<ChartTooltipContent />}
        />
        {appearance.legend && series.length > 1 && (
          <ChartLegend content={<ChartLegendContent />} />
        )}
        {series.map((s) => (
          <Line
            key={s.name}
            dataKey={s.name}
            type="natural"
            strokeWidth={2}
            stroke={lineStyleMap[appearance.lineStyle]}
            dot={appearance.showPoints ? { r: appearance.pointSize } : false}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}

function PieView({
  data,
  appearance,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "pie" }>>) {
  const slices = (data as any).slices as { label: string; value: number }[];

  const sliceColors = useMemo(() => {
    return slices.map((_slice, index) => {
      return resolveSeriesColor(
        index,
        appearance.paletteMode,
        appearance.presetCategoricalPaletteId,
        appearance.customColors
      );
    });
  }, [slices, appearance]);

  const chartConfig = useMemo(
    () =>
      slices.reduce(
        (acc, slice, i) => ({
          ...acc,
          [slice.label]: {
            label: slice.label,
            color: sliceColors[i],
          },
        }),
        {}
      ),
    [slices, sliceColors]
  );

  const enhancedSlices = useMemo(
    () =>
      slices.map((slice, i) => ({
        ...slice,
        fill: sliceColors[i],
      })),
    [slices, sliceColors]
  );

  return (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <PieChart
      // margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
      >
        <ChartTooltip
          labelFormatter={formatTooltipLabel}
          content={<ChartTooltipContent hideLabel />}
        />
        <Pie
          data={enhancedSlices}
          dataKey="value"
          nameKey="label"
          // paddingAngle={2}
        />
        {appearance.legend && (
          <ChartLegend content={<ChartLegendContent nameKey="label" />} />
        )}
      </PieChart>
    </ChartContainer>
  );
}

function HistogramView({
  data,
  appearance,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "histogram" }>>) {
  const bins = (data as any).bins as { label: string }[];
  const series = (data as any).series as { name: string; data: number[] }[];

  const chartData = useMemo(
    () =>
      bins.map((bin, i) => ({
        bin: String(bin.label),
        ...series.reduce(
          (acc, s) => ({ ...acc, [s.name]: s.data[i] ?? 0 }),
          {}
        ),
      })),
    [bins, series]
  );

  const chartColors = useMemo(
    () => getChartColors(series, appearance),
    [series, appearance]
  );

  const chartConfig = useMemo(
    () =>
      series.reduce(
        (acc, s, i) => ({
          ...acc,
          [s.name?.split(" ")?.join("")]: {
            label: s.name,
            color: chartColors[i],
          },
        }),
        {}
      ),
    [series, chartColors]
  );

  const xAxisFormatter = getNumberFormatter(appearance?.compactXAxis ?? false);
  const yAxisFormatter = getNumberFormatter(appearance?.compactYAxis ?? false);

  return (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <BarChart
        data={chartData}
        margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
      >
        {appearance.showGrid && <CartesianGrid vertical={false} />}
        {appearance.showXAxisLabels && (
          <XAxis
            dataKey="bin"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            angle={appearance.xAxisLabelRotation}
            height={appearance.xAxisLabelRotation !== 0 ? 48 : 24}
          />
        )}
        {appearance.showYAxisLabels && <YAxis allowDecimals tickMargin={6} tickFormatter={yAxisFormatter} />}
        <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
        {appearance.legend && series.length > 1 && (
          <ChartLegend content={<ChartLegendContent />} />
        )}
        {series.map((s) => (
          <Bar
            key={s.name}
            dataKey={s.name}
            radius={4}
            fill={`var(--color-${s.name?.split(" ")?.join("")})`}
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
}

function ScatterView({
  data,
  appearance,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "scatter" }>>) {
  const series = (data as any).series as {
    name: string;
    points: { x: number; y: number }[];
  }[];

  const chartColors = useMemo(
    () => getChartColors(series, appearance),
    [series, appearance]
  );

  const chartConfig = useMemo(
    () =>
      series.reduce(
        (acc, s, i) => ({
          ...acc,
          [s.name]: { label: s.name, color: chartColors[i] },
        }),
        {}
      ),
    [series, chartColors]
  );

  const xAxisFormatter = getNumberFormatter(appearance?.compactXAxis ?? false);
  const yAxisFormatter = getNumberFormatter(appearance?.compactYAxis ?? false);

  return (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <ScatterChart margin={{ top: 12, right: 12, bottom: 12, left: 12 }}>
        {appearance.showGrid && (
          <CartesianGrid
            stroke={appearance.gridColor}
            strokeDasharray={
              appearance.gridStyle === "dashed"
                ? "3 3"
                : appearance.gridStyle === "dotted"
                ? "1 3"
                : undefined
            }
          />
        )}
        {appearance.showXAxisLabels && (
          <XAxis
            type="number"
            dataKey="x"
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
            tickFormatter={xAxisFormatter}
          />
        )}
        {appearance.showYAxisLabels && (
          <YAxis
            type="number"
            dataKey="y"
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
            tickFormatter={yAxisFormatter}
          />
        )}
        <ChartTooltip
          cursor={{ strokeDasharray: "3 3" }}
          content={<ChartTooltipContent />}
        />
        {appearance.legend && series.length > 1 && (
          <ChartLegend content={<ChartLegendContent />} />
        )}
        {series.map((s, i) => (
          <Scatter
            key={s.name}
            name={s.name}
            data={s.points}
            fill={chartColors[i]}
          />
        ))}
      </ScatterChart>
    </ChartContainer>
  );
}

function CalendarHeatmapView({
  data,
  appearance,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "calendar-heatmap" }>>) {
  const values = (data as any).values as { date: string; value: number }[];
  const startDate = (data as any).startDate as Date | string;
  const endDate = (data as any).endDate as Date | string;

  const maxValue = useMemo(
    () => Math.max(1, ...values.map((v) => v.value)),
    [values]
  );

  return (
    <div className="flex h-full w-full items-center overflow-auto p-2 sm:p-3 md:p-4">
      <CalendarHeatmap
        startDate={startDate as any}
        endDate={endDate as any}
        values={values}
        // tooltipDataAttrs={(v: any) =>
        //   v?.date
        //     ? { "data-tip": `${formatDate(v.date)}: ${formatValue(v.value)}` }
        //     : {}
        // }
        transformDayElement={(rect: any, value: any, index: number) => {
          const fill = getSequentialColor(
            value?.value || 0,
            maxValue,
            (appearance.presetSequentialPaletteId ||
              "blue") as SequentialPaletteId
          );
          return React.cloneElement(rect, {
            key: index,
            style: { fill, rx: 2, cursor: "pointer" },
          });
        }}
      />
    </div>
  );
}

function MapView({ data, widget }: ChartViewProps<Extract<WidgetDataPayload, { type: "map" }>> & { widget: any }) {
  return (
    <div className="h-full w-full">
      <MapWidget data={data} widget={widget} />
    </div>
  );
}

function CrosstabView({
  data,
  appearance,
  widget,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "crosstab" }>> & { widget?: any }) {
  const rows = (data as any).rows as string[];
  const columns = (data as any).columns as string[];
  const values = (data as any).values as (number | string)[][];
  const rowTotals = (data as any).rowTotals as (number | string)[] | undefined;
  const colTotals = (data as any).colTotals as (number | string)[] | undefined;
  const grandTotal = (data as any).grandTotal as number | string | undefined;

  const formatCellValue = (value: number | string): string => {
    if (typeof value === 'number') {
      switch (appearance.numberFormat) {
        case 'rowPct':
          // This would need row totals - simplified for now
          return value.toLocaleString();
        case 'colPct':
          // This would need column totals - simplified for now
          return value.toLocaleString();
        case 'grandPct':
          // This would need grand total - simplified for now
          return value.toLocaleString();
        default:
          return value.toLocaleString();
      }
    }
    return String(value);
  };

  const getCellColor = (rowIndex: number, colIndex: number, value: number | string): string => {
    if (appearance.crosstabColoringMode === 'solid') {
      return appearance.valueColor || '#000000';
    } else if (appearance.crosstabColoringMode === 'heatmap' && typeof value === 'number') {
      // Use sequential color based on value
      const allNumericValues = values.flat().filter(v => typeof v === 'number') as number[];
      const maxValue = allNumericValues.length > 0 ? Math.max(...allNumericValues) : 1;
      const intensity = maxValue > 0 ? value / maxValue : 0;
      const paletteId = (appearance.presetSequentialPaletteId || 'blue') as SequentialPaletteId;
      return getSequentialColor(intensity, 1, paletteId);
    } else if (appearance.crosstabColoringMode === 'conditions' && typeof value === 'number') {
      // Condition-based coloring - first matching rule wins
      const conditions = appearance.crosstabConditions || [];
      for (const condition of conditions) {
        let matches = false;
        switch (condition.operator) {
          case 'equals':
            matches = value === condition.value;
            break;
          case 'not_equals':
            matches = value !== condition.value;
            break;
          case 'greater_than':
            matches = value > condition.value;
            break;
          case 'greater_or_equal':
            matches = value >= condition.value;
            break;
          case 'less_than':
            matches = value < condition.value;
            break;
          case 'less_or_equal':
            matches = value <= condition.value;
            break;
          case 'between':
            matches = value >= condition.value && value <= (condition.value2 || condition.value);
            break;
        }
        if (matches) {
          return condition.color;
        }
      }
      // No condition matched, fallback to default
      return appearance.valueColor || '#000000';
    }
    return appearance.valueColor || '#000000';
  };

  const cellPadding = appearance.compactDensity ? 'px-2 py-1' : 'px-4 py-2';
  const showBorders = appearance.cellBorder !== false;

  const rowAxisTitle = widget.config?.options?.crosstab?.rowAxisTitle;
  const colAxisTitle = widget.config?.options?.crosstab?.colAxisTitle;
  const totalRows = rows.length + (appearance.showColumnTotals ? 1 : 0);

  return (
    <div className="h-full w-full overflow-x-auto overflow-y-auto relative">
      {/* Scroll indicator for horizontal scrolling */}
      <div className="absolute top-0 right-0 bg-gradient-to-l from-white to-transparent w-8 h-full pointer-events-none z-10 sm:hidden" />
      <table className={`w-full h-full text-xs sm:text-sm ${showBorders ? 'border-collapse border border-gray-300' : ''}`}>
        <thead>
          {/* Merged header row for both axis titles and column headers */}
          <tr>
            {rowAxisTitle && (
              <th
                colSpan={2}
                rowSpan={colAxisTitle ? 2 : 1}
                className={`${cellPadding} bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}
              >
                {/* Merged space for vertical row title and row labels */}
              </th>
            )}
            {!rowAxisTitle && (
              <th
                rowSpan={colAxisTitle ? 2 : 1}
                className={`${cellPadding} bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}
              >
                {/* Empty space for row labels */}
              </th>
            )}
            {colAxisTitle && (
              <th
                colSpan={columns.length + (appearance.showRowTotals ? 1 : 0)}
                className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}
              >
                {colAxisTitle}
              </th>
            )}
            {!colAxisTitle && columns.map((col, i) => (
              <th key={i} className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                {col}
              </th>
            ))}
            {!colAxisTitle && appearance.showRowTotals && (
              <th className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                Total
              </th>
            )}
          </tr>
          {colAxisTitle && (
            <tr>
              {columns.map((col, i) => (
                <th key={i} className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                  {col}
                </th>
              ))}
              {appearance.showRowTotals && (
                <th className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                  Total
                </th>
              )}
            </tr>
          )}
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {rowAxisTitle && rowIndex === 0 && (
                <td
                  rowSpan={totalRows}
                  className={`${cellPadding} font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''} align-middle`}
                  style={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    transform: 'rotate(180deg)',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                    textAlign: 'center'
                  }}
                >
                  {rowAxisTitle}
                </td>
              )}
              <td className={`${cellPadding} font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                {row}
              </td>
              {columns.map((col, colIndex) => {
                const value = values[rowIndex]?.[colIndex] ?? 0;
                const color = getCellColor(rowIndex, colIndex, value);
                return (
                  <td
                    key={colIndex}
                    className={`${cellPadding} text-center ${showBorders ? 'border border-gray-300' : ''}`}
                    style={{ color }}
                  >
                    {formatCellValue(value)}
                  </td>
                );
              })}
              {appearance.showRowTotals && rowTotals && (
                <td className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                  {formatCellValue(rowTotals[rowIndex])}
                </td>
              )}
            </tr>
          ))}
          {appearance.showColumnTotals && colTotals && (
            <tr>
              <td className={`${cellPadding} font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                Total
              </td>
              {colTotals.map((total, colIndex) => (
                <td key={colIndex} className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                  {formatCellValue(total)}
                </td>
              ))}
              {appearance.showGrandTotal && grandTotal && (
                <td className={`${cellPadding} text-center font-bold bg-gray-100 ${showBorders ? 'border border-gray-300' : ''}`}>
                  {formatCellValue(grandTotal)}
                </td>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CCTView({
  data,
  appearance,
  widget,
}: ChartViewProps<Extract<WidgetDataPayload, { type: "cct" }>> & { widget?: any }) {
  // Feature flag: use legacy table if explicitly requested
  const useLegacyTable = widget?.config?.options?.cct?.legacyFallback === true;

  if (useLegacyTable) {
    // Fallback to original implementation
    const factors = (data as any).factors as string[];
    const measures = (data as any).measures as { id: string; label: string }[];
    const combinations = (data as any).combinations as string[][];
    const values = (data as any).values as (number | null)[][];

    const formatCellValue = (value: number | null): string => {
      if (value === null) return "-";
      if (typeof value === 'number') {
        switch (appearance.numberFormat) {
          case 'rowPct':
          case 'colPct':
          case 'grandPct':
            // Simplified for now - would need totals
            return value.toLocaleString();
          default:
            return value.toLocaleString();
        }
      }
      return String(value);
    };

    const getCellColor = (rowIndex: number, measureIndex: number, value: number | null): string => {
      if (value === null) return appearance.valueColor || '#000000';

      const measure = measures[measureIndex];
      const measureId = measure.id || `${(measure as any)?.fieldId}_${(measure as any)?.aggregation}` || measureIndex.toString();

      // Try multiple possible keys for measure coloring
      const possibleKeys = [
        measureId.replace(':', '_'), // Replace colon with underscore (primary)
        measureId, // Original with colon
        measureIndex.toString(), // Use index as string
      ];

      // Check if per-measure coloring is enabled
      let measureColoring = null;
      for (const key of possibleKeys) {
        if (appearance.cctMeasureColoring?.[key]) {
          measureColoring = appearance.cctMeasureColoring[key];
          break;
        }
      }

      if (appearance.cctColoringScope === 'per-measure' && measureColoring) {
        if (measureColoring.mode === 'solid') {
          return measureColoring.color || appearance.valueColor || '#000000';
        } else if (measureColoring.mode === 'heatmap' && typeof value === 'number') {
          // Heatmap coloring per measure
          const columnValues = values.map(row => row[measureIndex]).filter(v => v !== null && typeof v === 'number') as number[];
          const maxValue = columnValues.length > 0 ? Math.max(...columnValues) : 1;
          const intensity = maxValue > 0 ? value / maxValue : 0;
          // Map "purples" to "purple" since that's the available palette
          const paletteMap: Record<string, SequentialPaletteId> = {
            'purples': 'purple',
            'reds': 'red',
            'blues': 'blue',
            'greens': 'green',
            'teals': 'teal',
          };
          const mappedPalette = (measureColoring.palette && paletteMap[measureColoring.palette]) || (measureColoring.palette as SequentialPaletteId) || 'blue';
          return getSequentialColor(intensity, 1, mappedPalette);
        } else if (measureColoring.mode === 'conditions' && typeof value === 'number') {
          // Condition-based coloring per measure
          const conditions = measureColoring.conditions || [];
          for (const condition of conditions) {
            let matches = false;
            switch (condition.operator) {
              case 'equals':
                matches = value === condition.value;
                break;
              case 'not_equals':
                matches = value !== condition.value;
                break;
              case 'greater_than':
                matches = value > condition.value;
                break;
              case 'greater_or_equal':
                matches = value >= condition.value;
                break;
              case 'less_than':
                matches = value < condition.value;
                break;
              case 'less_or_equal':
                matches = value <= condition.value;
                break;
              case 'between':
                matches = value >= condition.value && value <= (condition.value2 || condition.value);
                break;
            }
            if (matches) {
              return condition.color;
            }
          }
          // No condition matched, fallback to measure color
          return measureColoring.color || appearance.valueColor || '#000000';
        }
      }

      // Fallback to global coloring modes
      if (appearance.crosstabColoringMode === 'solid') {
        if (appearance.cctColoringScope === 'per-measure') {
          // Per-measure coloring: use different color per column
          return resolveSeriesColor(
            measureIndex,
            appearance.paletteMode,
            appearance.presetCategoricalPaletteId,
            appearance.customColors
          );
        } else {
          // Global coloring: same color for all
          return appearance.valueColor || '#000000';
        }
      } else if (appearance.crosstabColoringMode === 'heatmap' && typeof value === 'number') {
        // Heatmap coloring
        let maxValue = 1;
        if (appearance.cctColoringScope === 'per-measure') {
          // Max per measure column
          const columnValues = values.map(row => row[measureIndex]).filter(v => v !== null && typeof v === 'number') as number[];
          maxValue = columnValues.length > 0 ? Math.max(...columnValues) : 1;
        } else {
          // Global max across all measure cells
          const allValues = values.flat().filter(v => v !== null && typeof v === 'number') as number[];
          maxValue = allValues.length > 0 ? Math.max(...allValues) : 1;
        }
        const intensity = maxValue > 0 ? value / maxValue : 0;
        return getSequentialColor(intensity, 1, appearance.presetSequentialPaletteId as SequentialPaletteId || 'blue');
      } else if (appearance.crosstabColoringMode === 'conditions' && typeof value === 'number') {
        // Condition-based coloring - first matching rule wins
        const conditions = appearance.crosstabConditions || [];
        for (const condition of conditions) {
          let matches = false;
          switch (condition.operator) {
            case 'equals':
              matches = value === condition.value;
              break;
            case 'not_equals':
              matches = value !== condition.value;
              break;
            case 'greater_than':
              matches = value > condition.value;
              break;
            case 'greater_or_equal':
              matches = value >= condition.value;
              break;
            case 'less_than':
              matches = value < condition.value;
              break;
            case 'less_or_equal':
              matches = value <= condition.value;
              break;
            case 'between':
              matches = value >= condition.value && value <= (condition.value2 || condition.value);
              break;
          }
          if (matches) {
            return condition.color;
          }
        }
        // No condition matched, fallback to default
        return appearance.valueColor || '#000000';
      }
      return appearance.valueColor || '#000000';
    };

    const cellPadding = appearance.compactDensity ? 'px-2 py-1' : 'px-4 py-2';
    const showBorders = appearance.cellBorder !== false;

    return (
      <div className="h-full w-full overflow-x-auto overflow-y-auto">
        <table className={`w-full h-full text-sm ${showBorders ? 'border-collapse border border-gray-300' : ''}`}>
          <thead>
            <tr>
              {factors.map((factor, i) => (
                <th key={i} className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                  {factor}
                </th>
              ))}
              {measures.map((measure, i) => (
                <th key={i} className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                  {measure.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {combinations.map((combo, rowIndex) => (
              <tr key={rowIndex}>
                {combo.map((factorValue, factorIndex) => (
                  <td key={factorIndex} className={`${cellPadding} font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                    {factorValue}
                  </td>
                ))}
                {measures.map((measure, measureIndex) => {
                  const value = values[rowIndex]?.[measureIndex] ?? null;
                  const color = getCellColor(rowIndex, measureIndex, value);
                  return (
                    <td
                      key={measureIndex}
                      className={`${cellPadding} text-center ${showBorders ? 'border border-gray-300' : ''}`}
                      style={{ color }}
                    >
                      {formatCellValue(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Use virtualized table by default
  return (
    <VirtualCCTTable
      data={{
        factors: (data as any).factors,
        measures: (data as any).measures,
        combinations: (data as any).combinations,
        values: (data as any).values,
      }}
      appearance={appearance}
      widget={widget}
    />
  );
}

